import React from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';

import env from 'env/env';

import ContentLocation from 'components/ContentLocation';
import MetaHeader from 'components/MetaHeader';
import NotFound from 'components/NotFound/NotFound';

import { formatRecipeMetaTitle, selectLandingPageImage } from 'utils/format-recipe';
import { getJsonLd } from 'components/CmsPage/utils';
import PageBreadcrumbs from 'components/Recipes/Content/Breadcrumbs/Breadcrumbs';

import pageTitleDataShape from 'constants/data-shapes/page-title';

import cmsPageStyles from '../CmsPage/CmsPage.scss';

import { RecipesContext } from './util';

const cmsPathToURL = path => path.replace('/content/waitrosegroceriescms/en/', '');

const RecipesLanding = props => {
  const {
    breadcrumbs,
    components,
    contentUrl,
    currentPage,
    metaData,
    pageTitle,
    isAllCategoriesPage,
    isFirstPage,
    isLastPage,
    pageNotFound,
    image,
  } = props;
  const landingPageImage = selectLandingPageImage(image);

  if (pageNotFound) return <NotFound />;

  const seoQuestionAndAnswerMetaData = getJsonLd(components);

  const pageParam = !isFirstPage && currentPage ? `?page=${currentPage}` : '';

  const canonicalHref = metaData.canonicalTag
    ? `https://${env.prodHost}/ecom/${cmsPathToURL(metaData.canonicalTag)}`
    : `https://${env.prodHost}/ecom/${contentUrl}`;

  const prevLink =
    !isAllCategoriesPage &&
    contentUrl !== 'recipes' &&
    !isFirstPage &&
    `${canonicalHref}${currentPage > 2 ? `?page=${currentPage - 1}` : ''}`;

  const nextLink =
    !isAllCategoriesPage &&
    contentUrl !== 'recipes' &&
    !isLastPage &&
    `${canonicalHref}?page=${parseInt(currentPage, 10) + 1}`;

  const metaTitle =
    // eslint-disable-next-line react/prop-types
    metaData && metaData?.title?.trim().length > 0 ? metaData.title : pageTitle?.text;

  const {
    turnOffIndexing = false,
    description: metaDescription = 'For 1,000s more tasty recipes visit waitrose.com',
    keywords: metaKeywords = '',
  } = metaData;

  const metaDataForHeader = {
    ...metaData,
    title: formatRecipeMetaTitle(metaTitle, false, currentPage),
    description: metaDescription,
    notIndexed: turnOffIndexing,
  };

  return (
    <>
      <MetaHeader
        {...metaDataForHeader}
        pageTitle={pageTitle}
        title={formatRecipeMetaTitle(metaTitle, false, currentPage)}
        canonicalHref={`${canonicalHref}${pageParam}`}
      />
      <Helmet>
        {prevLink && <link rel="prev" href={prevLink} data-testid="prevLink" />}
        {nextLink && currentPage && <link rel="next" href={nextLink} data-testid="nextLink" />}
        <meta data-testid="og:url" property="og:url" content={canonicalHref} />
        <meta data-testid="og:type" property="og:type" content="website" />
        <meta
          data-testid="og:title"
          property="og:title"
          content={
            seoQuestionAndAnswerMetaData
              ? `${formatRecipeMetaTitle(metaTitle, false, 1)} | Waitrose & Partners`
              : metaTitle
          }
        />
        <meta data-testid="og:description" property="og:description" content={metaDescription} />
        <meta data-testid="keywords" name="keywords" content={metaKeywords} />
        {landingPageImage && (
          <meta data-testid="og:image" property="og:image" content={landingPageImage} />
        )}
        <meta data-testid="twitter:card" name="twitter:card" content="summary_large_image" />
        {seoQuestionAndAnswerMetaData && (
          <script type="application/ld+json" data-test="seo-meta-data-faqs">
            {JSON.stringify(seoQuestionAndAnswerMetaData)}
          </script>
        )}
      </Helmet>
      <PageBreadcrumbs breadcrumbs={breadcrumbs} title={pageTitle.text} />
      <div className={cmsPageStyles.content}>
        <RecipesContext.Provider value={{ isAllCategoriesPage }}>
          <ContentLocation name="root" />
        </RecipesContext.Provider>
      </div>
    </>
  );
};

RecipesLanding.displayName = 'RecipesLanding';

RecipesLanding.defaultProps = {
  backToLabel: null,
  breadcrumbs: {},
  components: [],
  contentUrl: null,
  currentPage: 1,
  error: false,
  hash: null,
  loading: false,
  metaData: {},
  pageTitle: {},
  image: null,
  recipesByTags: [],
  isAllCategoriesPage: false,
  isFirstPage: false,
  isLastPage: false,
  pageNotFound: false,
};

RecipesLanding.propTypes = {
  backToLabel: PropTypes.string,
  breadcrumbs: PropTypes.shape({
    breadcrumbs: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        path: PropTypes.string.isRequired,
      }),
    ),
  }),
  components: PropTypes.arrayOf(
    PropTypes.shape({
      componentId: PropTypes.string,
      fragmentData: PropTypes.shape({
        componentId: PropTypes.string,
        resourceType: PropTypes.string,
      }),
    }),
  ),
  contentUrl: PropTypes.string,
  currentPage: PropTypes.number,
  error: PropTypes.bool,
  hash: PropTypes.string,
  loading: PropTypes.bool,
  metaData: PropTypes.shape({
    ...MetaHeader.propTypes,
    canonicalTag: PropTypes.string,
    description: PropTypes.string,
    turnOffIndexing: PropTypes.bool,
    keywords: PropTypes.string,
  }),
  pageTitle: pageTitleDataShape(),
  image: PropTypes.shape({
    fileReference: PropTypes.string,
    alternativeText: PropTypes.string,
    dynamicImages: PropTypes.shape({
      highQuality95: PropTypes.string,
    }),
  }),
  recipesByTags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      totalTime: PropTypes.number,
      images: PropTypes.arrayOf(
        PropTypes.shape({
          fileReference: PropTypes.string,
          alternativeText: PropTypes.string,
        }),
      ),
    }),
  ),
  isAllCategoriesPage: PropTypes.bool,
  isFirstPage: PropTypes.bool,
  isLastPage: PropTypes.bool,
  pageNotFound: PropTypes.bool,
};

export default RecipesLanding;
